/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Collapse, Divider, MenuItem, Paper, TextField } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import clsx from 'clsx';
import { RoleChipLabel } from 'components/common/RoleChip';
import { UserRoles } from 'shared/enums/Roles';
import { useSelector } from 'react-redux';
import ClearIcon from '@material-ui/icons/Clear';
import useTableFilter from './useTableFilter';
import FilterIcon from '../../common/FilterIcon';
import { InputType } from './FilterType';
import { trimToSize } from '../../../shared/utils/stringUtils';
import Button from '../../common/Button';
import { getSubmissionStatusById, selectFailedStatuses, SubmissionStatus } from '../../../models/SubmissionStatus';
import Tooltip from '../../common/Tooltip';
import DatePicker from '../../submission/templates/DatePicker';
import { shortFormatDate } from '../../../shared/utils/dateUtils';
import { useConfiguration } from '../../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../../redux/enums/ConfigResourceProps';
import { ChipLabel } from '../../common/StatusChip';
import { selectVersion } from '../../../redux/features/configuration/configuration.slice';
import { dataTestId } from '../../../shared/utils/versionUtils';
import { InvitationStatus } from '../../../redux/features/invitations/invitations.enum';

const useStyles = filterableColumnsCount =>
  makeStyles(theme => ({
    ...theme.customTheme,
    tooltip: {
      fontSize: '0.9em',
    },
    filterContainer: {
      width: '100%',
      marginTop: '.25rem',
      marginBottom: '.825rem',
      marginLeft: '.25rem',
    },
    filterBadge: {
      backgroundColor: theme.palette.counterBackground,
      color: theme.palette.primary.contrastText,
    },
    filterButton: {
      alignSelf: 'flex-start',
      outline: 'none',
      backgroundColor: theme.palette.filterButtonbackground,
      color: theme.palette.filterButtonText,
      '&:focus, &:active': {
        background: `${theme.palette.white}`,
      },
    },
    filterButtonOpen: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    filterChip: {
      position: 'relative',
      fontWeight: 'bold',
      marginBottom: '.825rem',
      marginLeft: '.3rem',
      marginRight: '.7rem',
      marginTop: 0,
      paddingLeft: '.3rem',
      color: theme.palette.statusChip.default.contrastText,
      '&::before': {
        content: '',
        fontWeight: 'normal',
      },
      '&:hover': {
        color: theme.palette.statusChip.default.contrastText,
      },
    },
    filterClearButton: {
      height: '1.25rem',
      marginRight: '1rem',
      top: '0.125rem',
      whiteSpace: 'nowrap',
      fontSize: '1rem',
      textDecoration: 'underline',
      color: `${theme.palette.linkColour}`,
      '&:hover': {
        textDecoration: 'underline',
        color: `${theme.palette.buttonTextHover} !important`,
      },
      '&:focus': {
        textDecoration: 'underline',
        outline: `calc(2rem / 16) solid ${theme.palette.buttonTextHover}`,
      },
    },
    filterDivider: {
      marginBottom: '1rem',
      marginRight: '1rem',
      marginTop: '1rem',
    },
    filterField: {
      margin: '0.5rem 1rem',
      [theme.breakpoints.up('md')]: {
        width: 'calc(100%/3)',
      },
      [theme.breakpoints.up('lg')]: {
        width: `calc((100% - 3rem) / ${filterableColumnsCount} )`,
      },
      '& .MuiInputLabel-formControl': {
        color: theme.palette.lightBorder,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        maxWidth: '85%',
      },
    },
    boxFilter: {
      [theme.breakpoints.down('md')]: {
        display: 'flex',
        flexWrap: 'wrap',
      },
      [theme.breakpoints.down('sm')]: {
        display: 'grid',
      },
    },
    dateField: {
      margin: '0.5rem 1rem',
    },
    chipBadge: {
      width: '1rem',
      height: '1rem',
      marginTop: '0.2rem',
      right: '-0.1rem',
    },
    filterIcon: {
      alignSelf: 'flex-start',
      backgroundColor: theme.palette[theme.filtersButtonColor]?.main,
      color: theme.palette[theme.filtersButtonColor]?.contrastText,
      '&:focus, &:active': {
        backgroundColor: theme.palette[theme.filtersButtonColor]?.active,
      },
      '&:hover': {
        backgroundColor: theme.palette[theme.filtersButtonColor]?.hover,
      },
      '& svg': {
        color: theme.palette[theme.filtersButtonColor]?.contrastText,
      },
    },
    filterNumbers: {
      backgroundColor: theme.palette.counterBackground,
      borderRadius: '25%',
      fontSize: '1rem',
      marginLeft: '0.75rem',
      minWidth: '1.5rem',
      padding: '0.1rem 0.25rem',
      lineHeight: 'normal',
      color: theme.palette.primary.contrastText,
    },
    filterPaper: {
      borderRadius: '0.5rem',
      borderTopLeftRadius: 0,
      boxShadow: 'none',
      padding: '1rem',
    },
    statusChip: {
      '&::before': {
        content: '',
        fontWeight: 'normal',
      },
    },
    roleChip: {
      '&::before': {
        content: '',
        fontWeight: 'normal',
      },
    },
  }));

function filterChip(filterElement, classes, deleteFilter, t, frontendCustomisationDateFormat, applicableFilters) {
  let { inputType } = filterElement;
  if (!inputType) {
    inputType = applicableFilters?.find(applicableFilter => applicableFilter.type === filterElement.type)?.inputType;
  }

  switch (inputType) {
    case InputType.STATUS:
      return (
        <Chip
          className={clsx(classes.filterChip, `${classes.statusChip}${filterElement.value ?? 'default'}`)}
          key={filterElement.id}
          onDelete={() => {
            deleteFilter(filterElement);
          }}
          deleteIcon={<ClearIcon />}
          label={(
            <ChipLabel
              status={filterElement?.value}
              isFilterDraftValid={getSubmissionStatusById(filterElement.value)?.valid}
              hasError={selectFailedStatuses(filterElement.value)}
              role={filterElement.value}
            />
          )}
          size="small"
        />
      );
    case InputType.INVITATION_STATUS:
      return (
        <Chip
          className={clsx(classes.filterChip, `${classes.statusChip}${filterElement.value ?? 'default'}`)}
          key={filterElement.id}
          deleteIcon={<ClearIcon />}
          onDelete={() => {
            deleteFilter(filterElement);
          }}
          label={<ChipLabel status={filterElement?.value} role={filterElement.value} />}
          size="small"
        />
      );
    case InputType.ROLES:
      return (
        <Chip
          className={clsx(classes.filterChip, `${classes.roleChip}${filterElement.value ?? 'default'}`)}
          key={filterElement.id}
          deleteIcon={<ClearIcon />}
          onDelete={() => {
            deleteFilter(filterElement);
          }}
          label={<RoleChipLabel role={filterElement.value} />}
          size="small"
        />
      );
    case InputType.DATE_PICKER:
      return (
        <React.Fragment key={filterElement.id}>
          <Tooltip classes={{ tooltip: classes.tooltip }} title={filterElement.value}>
            <Chip
              size="small"
              key={filterElement.id}
              className={classes.filterChip}
              label={`${trimToSize(t(filterElement.type), 10)}: ${shortFormatDate(
                filterElement.value,
                frontendCustomisationDateFormat,
              )}`}
              deleteIcon={<ClearIcon />}
              onDelete={() => {
                deleteFilter(filterElement);
              }}
            />
          </Tooltip>
        </React.Fragment>
      );
    default:
      return (
        <React.Fragment key={filterElement.id}>
          <Tooltip classes={{ tooltip: classes.tooltip }} title={filterElement.value}>
            <Chip
              size="small"
              key={filterElement.id}
              className={classes.filterChip}
              label={`${trimToSize(t(filterElement.type), 10)}: ${trimToSize(t(filterElement.value), 16)}`}
              deleteIcon={<ClearIcon />}
              onDelete={() => {
                deleteFilter(filterElement);
              }}
            />
          </Tooltip>
        </React.Fragment>
      );
  }
}

export default function TableFilter({
  isLargeScreen,
  filter,
  setFilter,
  updatePage,
  isAddressBookPage,
  columnsConfiguration,
}) {
  const { t } = useTranslation();
  const frontendCustomisation = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const frontendCustomisationDateFormat = frontendCustomisation.resource?.dateFormat;

  const {
    addDateFilter,
    addStatusFilter,
    addRolesFilter,
    clearFilterAction,
    deleteFilter,
    enterHandler,
    filters,
    showFilterDetails,
    toggleFilterDetails,
    applicableFilters,
    statusValues,
  } = useTableFilter(filter, setFilter, updatePage, columnsConfiguration);
  const classes = useStyles(applicableFilters?.length)();
  const selectedVersion = useSelector(selectVersion);

  return (
    <Box className={classes.filterContainer}>
      <Button
        {...dataTestId({ value: `tableFiltersCollapseButton--${selectedVersion.value}` })}
        disabled={isAddressBookPage}
        aria-label={showFilterDetails ? t('close.filters') : t('open.filters')}
        color="secondary"
        className={clsx({
          [classes.filterButton]: true,
          [classes.filterButtonOpen]: showFilterDetails,
        })}
        onClick={!isAddressBookPage && toggleFilterDetails}
        startIcon={<FilterIcon />}
      >
        {t('Filters')}
        {filters.length > 0 ? <Box className={classes.filterNumbers}>{filters.length}</Box> : ''}
      </Button>

      <Collapse in={showFilterDetails}>
        <Paper className={classes.filterPaper}>
          <form aria-label={t('Filters')}>
            <Box display={isLargeScreen ? 'flex' : 'grid'} className={classes.boxFilter}>
              {applicableFilters?.length > 0 &&
                applicableFilters.map((applicableFilter) => {
                  if (applicableFilter.inputType === InputType.DATE_PICKER) {
                    return (
                      <DatePicker
                        key={applicableFilter.label}
                        id={applicableFilter.id || 'filter-datepicker'}
                        clearable={false}
                        disableFuture={true}
                        value={applicableFilter.value || null}
                        className={classes.dateField}
                        label={t(`${applicableFilter.label}`)}
                        onChange={event => addDateFilter(event, applicableFilter.type)}
                      />
                    );
                  }

                  if (applicableFilter.inputType === InputType.ROLES) {
                    return (
                      <TextField
                        key={applicableFilter.label}
                        aria-label={t(`${applicableFilter.label}`)}
                        className={`${classes[applicableFilter.className]}`}
                        label={t(`${applicableFilter.label}`)}
                        onChange={event => addRolesFilter(event, applicableFilter.type)}
                        onKeyPress={event => addRolesFilter(event, applicableFilter.type)}
                        size="small"
                        select
                        value={applicableFilter.value || ''}
                        variant="outlined"
                        inputProps={{ 'data-testid': `${applicableFilter.testId}` }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                            PaperProps: {
                              style: {
                                maxHeight: '20rem',
                              },
                            },
                          },
                        }}
                      >
                        {Object.keys(UserRoles).map((key) => {
                          return (
                            <MenuItem
                              key={key}
                              value={UserRoles[key].id}
                              data-testid={`statusMenu-option${UserRoles[key].id}`}
                            >
                              <Chip
                                className={`${classes.roleChip}${UserRoles[key].id ?? 'default'}`}
                                size="small"
                                key={UserRoles[key].id}
                                label={<RoleChipLabel role={UserRoles[key].id} />}
                              />
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    );
                  }

                  if (applicableFilter.inputType === InputType.STATUS) {
                    return (
                      <TextField
                        key={applicableFilter.label}
                        aria-label={t(`${applicableFilter.label}`)}
                        className={`${classes[applicableFilter.className]}`}
                        label={t(`${applicableFilter.label}`)}
                        onChange={event => addStatusFilter(event, applicableFilter.type)}
                        onKeyPress={event => addStatusFilter(event, applicableFilter.type)}
                        size="small"
                        select
                        value={applicableFilter.value || ''}
                        variant="outlined"
                        inputProps={{ 'data-testid': `${applicableFilter.testId}` }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                            PaperProps: {
                              style: {
                                maxHeight: '20rem',
                              },
                            },
                          },
                        }}
                      >
                        {Object.keys(statusValues).map((key) => {
                          if (!SubmissionStatus[key]?.visible) {
                            return null;
                          }
                          return (
                            <MenuItem
                              key={key}
                              value={SubmissionStatus[key].id}
                              data-testid={`statusMenu-option${SubmissionStatus[key].id}`}
                            >
                              <Chip
                                className={`${classes.statusChip}${SubmissionStatus[key].id ?? 'default'}`}
                                size="small"
                                key={SubmissionStatus[key].id}
                                label={(
                                  <ChipLabel
                                    status={SubmissionStatus[key].id}
                                    valid={false}
                                    isFilterDraftValid={SubmissionStatus[key].valid}
                                    isPageInfo={false}
                                    hasError={SubmissionStatus[key].type === 'error'}
                                  />
                                )}
                              />
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    );
                  }

                  if (applicableFilter.inputType === InputType.INVITATION_STATUS) {
                    return (
                      <TextField
                        key={applicableFilter.label}
                        aria-label={t(`${applicableFilter.label}`)}
                        className={`${classes[applicableFilter.className]}`}
                        label={t(`${applicableFilter.label}`)}
                        onChange={event => addStatusFilter(event, applicableFilter.type)}
                        onKeyPress={event => addStatusFilter(event, applicableFilter.type)}
                        size="small"
                        select
                        value={applicableFilter.value || ''}
                        variant="outlined"
                        inputProps={{ 'data-testid': `${applicableFilter.testId}` }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                            PaperProps: {
                              style: {
                                maxHeight: '20rem',
                              },
                            },
                          },
                        }}
                      >
                        {Object.keys(InvitationStatus).map((key) => {
                          return (
                            <MenuItem
                              key={key}
                              value={InvitationStatus[key]}
                              data-testid={`statusMenu-option${InvitationStatus[key]}`}
                            >
                              <Chip
                                className={`${classes.statusChip}${InvitationStatus[key] ?? 'default'}`}
                                size="small"
                                key={InvitationStatus[key]}
                                label={<ChipLabel status={InvitationStatus[key]} valid={false} isPageInfo={false} />}
                              />
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    );
                  }
                  return (
                    <TextField
                      key={applicableFilter.label}
                      aria-label={t(`${applicableFilter.label}`)}
                      id={applicableFilter.label}
                      className={`${classes[applicableFilter.className]}`}
                      label={t(`${applicableFilter.label}`)}
                      onKeyPress={event => enterHandler(event, applicableFilter.type)}
                      size="small"
                      variant="outlined"
                      inputProps={{ 'data-testid': `${applicableFilter.testId}` }}
                    />
                  );
                })}
            </Box>
          </form>

          <Divider className={filters?.length === 0 ? classes.hidden : classes.filterDivider} />
          <Box display={isLargeScreen ? 'flex' : 'grid'}>
            <Box>
              {filters.map(filterElement =>
                filterChip(filterElement, classes, deleteFilter, t, frontendCustomisationDateFormat, applicableFilters),
              )}
            </Box>
            <Button
              {...dataTestId({ value: `"tableFiltersClearButton--${selectedVersion.value}` })}
              aria-label={t('Clear')}
              onClick={clearFilterAction}
              className={filters?.length === 0 ? classes.hidden : classes.filterClearButton}
              variant="text"
              color="primary"
              size="small"
            >
              {t('Clear')}
            </Button>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
}
